'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ACCOUNT_CONNECTION_EMPTY_CLASS } from '@/features/dashboard/components/account-connection/styles'
import { useLighterOnboard } from '@/hooks/use-lighter-onboard'
import {
  canSubmit,
  isSlotResolved,
  type SelectionState,
  setConfirmed,
  setConfirming,
  setNone,
  setPrepared,
  setPreparing,
  type SlotSelection,
  toggleSlot,
} from '@/lib/lighter/subaccount-selection'
import { cn } from '@/lib/utils'
import type { LighterSubaccountInfo } from '@/types/generated/public_api'

const DEFAULT_MAX_SLOTS_PER_LINK = 5

type LighterSubaccountPickerProps = {
  subaccounts: LighterSubaccountInfo[]
  accountId: string
  l1Address: string
  selection: SelectionState
  onChange: (selection: SelectionState | ((prev: SelectionState) => SelectionState)) => void
  onSubmit: () => void
  submitting: boolean
  isPageLayout?: boolean
  // How many account slots the customer's plan still allows. Absent => only
  // the per-link cap applies.
  remainingSlots?: number
}

export function LighterSubaccountPicker({
  subaccounts,
  accountId,
  l1Address,
  selection,
  onChange,
  onSubmit,
  submitting,
  isPageLayout = false,
  remainingSlots,
}: LighterSubaccountPickerProps) {
  const t = useTranslations('accountConnection')
  const { prepareKey, signAndConfirm } = useLighterOnboard()
  const [mintError, setMintError] = useState<Record<string, string>>({})

  // Clamp to the plan's remaining slots, as Derive and Hyperliquid's pickers
  // both do. Without this a user with 1 slot left can check 3 rows, perform 3
  // separate wallet signatures — each one causing the backend to mint a
  // keypair and register it on Lighter via ChangePubKey — and only then land
  // on a results screen with 2 failures. Two on-chain registrations and two
  // signing prompts spent on selections we could have refused up front.
  const maxSlots =
    remainingSlots === undefined
      ? DEFAULT_MAX_SLOTS_PER_LINK
      : Math.max(0, Math.min(DEFAULT_MAX_SLOTS_PER_LINK, remainingSlots))
  const submittable = canSubmit(selection)

  // Unlike Derive's single-slot wizard flow (account-connection-wizard.tsx's
  // handlePrepareAccount, account-connection-sheet.tsx), where exactly one
  // async onboarding sequence is ever in flight at a time, this picker is
  // multi-slot: a user can click "Sign with wallet" on row A, then click it
  // on row B before A resolves. Each `handleMint` call is its own independent
  // async sequence closing over the `accountIndex` it was invoked with, and
  // two can be in flight concurrently touching the same shared selection
  // state. Every `onChange` call below uses the functional-updater form
  // (`(prev) => ...`) rather than a plain value threaded through a local
  // `current` variable, so each transition composes from whatever the LATEST
  // state actually is at application time — React guarantees a functional
  // updater always sees the true latest state, even across two overlapping
  // handlers — instead of a snapshot that goes stale the moment a second row
  // starts mutating state concurrently. A plain-value `current` chain (the
  // Derive-precedent approach) would let row A's later, slower onChange calls
  // overwrite row B's already-committed progress, wedging row B's slot.
  const handleMint = async (slot: SlotSelection) => {
    const accountIndex = slot.accountIndex
    setMintError((prev) => ({ ...prev, [accountIndex]: '' }))
    // Reuse a key an earlier attempt already minted. venue.api.ts documents
    // the contract explicitly: on a failed confirm the pending entry stays
    // retriable and must be re-confirmed with the SAME preparedKeyId. Starting
    // over from prepare mints and registers a second keypair and orphans the
    // first — a network blip after a successful wallet signature was enough.
    let preparedKeyId =
      slot.keySource.type === 'prepared' || slot.keySource.type === 'confirming'
        ? slot.keySource.preparedKeyId
        : undefined
    let personalSignText =
      slot.keySource.type === 'prepared' ? slot.keySource.personalSignText : undefined
    try {
      if (!preparedKeyId || !personalSignText) {
        onChange((prev) => setPreparing(prev, accountIndex))
        // apiKeyIndex must match what linkSubaccounts submits for this slot;
        // hardcoding 0 here agreed with the seed only by coincidence.
        const prepared = await prepareKey(accountId, l1Address, accountIndex, slot.apiKeyIndex)
        preparedKeyId = prepared.preparedKeyId
        personalSignText = prepared.personalSignText
        onChange((prev) => setPrepared(prev, accountIndex, preparedKeyId!, personalSignText!))
      }
      onChange((prev) => setConfirming(prev, accountIndex, preparedKeyId!))
      const result = await signAndConfirm(l1Address, preparedKeyId, personalSignText)
      // `error` is a proto3 string: its default is '', which would surface as
      // an Error with an empty message — falsy, so the error <span> below
      // never renders and the row silently snaps back with no explanation.
      if (!result.ok) throw new Error(result.error || t('lighterLinkError'))
      onChange((prev) => setConfirmed(prev, accountIndex, preparedKeyId!))
    } catch (e) {
      setMintError((prev) => ({
        ...prev,
        [accountIndex]: e instanceof Error && e.message ? e.message : t('lighterLinkError'),
      }))
      onChange((prev) =>
        preparedKeyId && personalSignText
          ? setPrepared(prev, accountIndex, preparedKeyId, personalSignText)
          : setNone(prev, accountIndex)
      )
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {subaccounts.length === 0 ? (
        <p className={ACCOUNT_CONNECTION_EMPTY_CLASS}>{t('lighterPickerEmpty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {subaccounts.map((sub) => {
            const slot = selection.slots.find((s) => s.accountIndex === sub.accountIndex)
            const checked = !!slot
            const disabled = sub.alreadyLinked || (!checked && selection.slots.length >= maxSlots)
            return (
              <div
                key={sub.accountIndex}
                className={cn(
                  'flex flex-col gap-3 border border-white/10 bg-white/5 p-3',
                  isPageLayout && 'border-foreground/10 bg-foreground/5'
                )}
              >
                <label
                  className={cn(
                    'flex items-center gap-3',
                    disabled ? 'opacity-60' : 'cursor-pointer'
                  )}
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    // Functional-updater form, like every other onChange here
                    // — see the invariant above. Building from the render
                    // closure's `selection` and replacing the whole state
                    // discards a concurrent row's just-committed transition.
                    onCheckedChange={() =>
                      onChange((prev) =>
                        toggleSlot(
                          prev,
                          sub.accountIndex,
                          { alreadyLinked: sub.alreadyLinked },
                          maxSlots
                        )
                      )
                    }
                  />
                  <span className="truncate text-sm font-medium">
                    {t('lighterSubaccountLabel', { id: sub.accountIndex })}
                  </span>
                  {sub.alreadyLinked && (
                    <span className="border-foreground/15 bg-foreground/8 text-foreground shrink-0 border px-2 py-0.5 text-[10px] font-medium">
                      {t('lighterSubaccountConnected')}
                    </span>
                  )}
                  {sub.alreadyLinked && sub.linkedAccountId && (
                    <span className="text-xs text-white/50">
                      {t('lighterSubaccountLinkedTo', { account: sub.linkedAccountId })}
                    </span>
                  )}
                </label>

                {checked && slot && !isSlotResolved(slot) && (
                  <div className="flex flex-col gap-2 pl-7">
                    <span className="text-xs text-white/50">{t('lighterMintKeyLabel')}</span>
                    <Button
                      variant="outline"
                      type="button"
                      size="sm"
                      disabled={
                        slot.keySource.type === 'preparing' || slot.keySource.type === 'confirming'
                      }
                      onClick={() => handleMint(slot)}
                    >
                      {slot.keySource.type === 'preparing'
                        ? t('lighterMintKeyPreparing')
                        : slot.keySource.type === 'confirming'
                          ? t('lighterMintKeyConfirming')
                          : t('lighterMintKeyButton')}
                    </Button>
                    {mintError[sub.accountIndex] && (
                      <span className="text-destructive text-xs">
                        {mintError[sub.accountIndex]}
                      </span>
                    )}
                  </div>
                )}
                {checked && slot?.keySource.type === 'confirmed' && (
                  <span className="text-chart-mint pl-7 text-xs">
                    {t('lighterMintKeyConfirmed')}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}

      <p className={cn('text-center text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
        {t('lighterSlotsMeter', { count: selection.slots.length, max: maxSlots })}
      </p>

      <Button
        variant="basic"
        type="button"
        className="mx-auto w-fit"
        onClick={onSubmit}
        disabled={!submittable || submitting}
      >
        {submitting ? t('lighterLinking') : t('lighterLinkButton')}
      </Button>
    </div>
  )
}
